<template>
	<v-card class="forum-preview-card elevation-0 d-flex flex-column pt-2 mb-1">
		<div class="forum-preview-card__container">
			<div class="forum-preview-card__outer">
				<div class="d-flex forum-preview-card__list pl-0 pr-0">
					<div class="forum-preview-card__list__info">
						<div
							@click="toggleToUser(forum.user.user_id)"
							:style="{
								cursor: canOpenProfile(forum.user.user_id) ? 'pointer' : 'default',
							}"
							class="text-decoration-none"
							:class="{ disabled: forum.user.user_hidden }"
						>
							<div class="forum-preview-card__header">
								<div class="d-flex align-center">
									<Avatar
										class="mr-2 category-card__avatar"
										:size="52"
										:avatar="forum.user.user_logo"
									/>
									<div class="forum-preview-card__meta">
										<span class="forum-preview-card__meta-top">
											{{ forum.user.visible_username }}
										</span>
										<span class="forum-preview-card__meta-bottom">{{
											createdTime
										}}</span>
									</div>
								</div>
							</div>
						</div>
						<SponsorBadge v-if="forum.pin_to_top" :icon="'pinned'" :iconSize="16" />
						<div
							class="forum-preview-card__title"
							@click="toggleToForum"
							v-if="forum && forum.thread_title"
						>
							{{ forum.thread_title }}
						</div>

						<div v-if="forum && forum.thread_text" class="u-cursor-pointer">
							<ExpandableHtml
								:content="forum.thread_text"
								:expandText="'See More'"
								:collapseText="'Hide'"
								@handleClick="toggleToForum"
							/>
						</div>
						<div
							class="forum-preview-card__image-container"
							v-if="forum && forum.slider_images.length > 0 && isShowSlider"
							@click="toggleToForum"
						>
							<div
								class="forum-preview-card__image-container__item"
								v-for="(image, index) in forum.slider_images.slice(0, 5)"
								:key="image.upload_id"
							>
								<img
									:src="image.image_url"
									class="forum-preview-card__image-container__img"
									alt="thread image"
								/>

								<div
									v-if="forum.slider_images.length > 5 && index === 4"
									class="forum-preview-card__image-container__overlay"
								>
									+{{ forum.slider_images.length - 5 }}
								</div>
							</div>
						</div>
					</div>

					<v-divider />

					<div class="d-flex justify-space-between align-center px-3 py-3">
						<div class="forum-preview-card__list__icons-container">
							<div
								@click="onLike"
								class="forum-preview-card__list__icons-container__icon-info u-cursor-pointer"
							>
								<Icon
									:isActive="!!isLiked"
									type="hearth"
									:color="isLiked ? 'var(--primary_color)' : '#9b9b9b'"
									:size="24"
								/>
								<div
									class="forum-preview-card__list__icons-container__icon-info__counter"
								>
									{{ likes }}
								</div>
							</div>
							<div
								@click="toggleToForum"
								class="forum-preview-card__list__icons-container__icon-info u-cursor-pointer"
							>
								<Icon type="comment" color="#9b9b9b" :size="24" />
								<div
									class="forum-preview-card__list__icons-container__icon-info__counter"
								>
									{{ forum.comments_count }}
								</div>
							</div>
							<div
								v-if="forum.uploads.length > 0"
								@click="toggleToFiles"
								class="forum-preview-card__list__icons-container__icon-info u-cursor-pointer"
							>
								<Icon type="clip" color="#9b9b9b" :size="24" />
								<div
									class="forum-preview-card__list__icons-container__icon-info__counter"
								>
									{{ forum.uploads.length }}
								</div>
							</div>
						</div>
						<div
							@click="addToBookmark"
							class="forum-preview-card__follow u-cursor-pointer"
						>
							<Icon
								:type="bookmarked ? 'check' : 'plus'"
								:color="bookmarked ? '#9b9b9b' : 'var(--primary_color)'"
								:size="24"
							/>
							<span
								:style="{ color: bookmarked ? '#9b9b9b' : 'var(--primary_color)' }"
								class="forum-preview-card__follow__status"
								>{{ bookmarked ? 'Following' : 'Follow' }}</span
							>
						</div>
					</div>
				</div>
			</div>
		</div>
	</v-card>
</template>

<script>
import Vue from 'vue'
import VueScrollTo from 'vue-scrollto'
import { mapActions, mapGetters } from 'vuex'
import Avatar from '@/components/Avatar'
import Dwh from '@/mixins/Dwh'
import ExpandableHtml from '@/components/ExpandableHtml.vue'
import { toggleDislike, toggleLike } from '@/api/post'
import { calcDateAgo } from '@/utils/helpers'
import SponsorBadge from '@/components/SponsorBadge.vue'

Vue.use(VueScrollTo)

export default {
	name: 'ForumPreview',
	mixins: [Dwh],
	components: {
		SponsorBadge,
		Avatar,
		ExpandableHtml,
	},
	data() {
		return {
			isLiked: false,
			likes: 0,
			bookmarked: null,
		}
	},
	props: {
		forum: {
			type: Object,
			default: () => ({}),
		},
		isShowSlider: {
			type: Boolean,
			default: true,
		},
		isMyBookmark: {
			type: Boolean,
			default: true,
		},
	},
	methods: {
		...mapActions('event', ['toggleBookmark']),
		async addToBookmark() {
			const newStatus = this.bookmarked ? 0 : 1
			await this.toggleBookmark({
				user_id: this.currentUser.user_id,
				bookmark_entity_type: this.forum.bookmark.bookmark_entity_type,
				bookmark_status: newStatus,
				bookmark_entity_id: this.forum.bookmark.bookmark_entity_id,
			})
			this.bookmarked = newStatus
		},
		onLike() {
			if (this.isLiked) {
				if (this.likes === 0) {
					return
				}
				this.likes--
				toggleDislike(this.forum.thread_id)
			} else if (!this.isLiked) {
				this.likes++
				toggleLike({ thread_id: this.forum.thread_id })
			}

			this.isLiked = !this.isLiked
		},
		canOpenProfile(id) {
			const isMe = this.isCurrentUser(id)
			return isMe || this.hasOtherProfileAccess
		},
		toggleToUser(id) {
			const isMe = this.isCurrentUser(id)
			if (!isMe && !this.hasOtherProfileAccess) {
				return
			}
			if (isMe) {
				this.$router.push({ name: 'my-account' })
			} else {
				this.$router.push({ name: 'user-by-id', params: { id } })
			}
		},
		dwhMyBookmarkHandler() {
			this.sendTrigger({
				category: 'Bookmarks',
				action: 'click_bookmarked_discussion_feed',
				module: 'Forums / Communities',
				title: this.forum.thread_title,
				id1: this.forum.thread_id,
			})
		},
		toggleToForum() {
			if (!this.isMyBookmark) {
				this.dwhMyBookmarkHandler()
			}
			this.$router.push({
				name: 'forum-by-id',
				params: { id: this.forum.thread_id },
			})
		},
		toggleToFiles() {
			this.$router.push({
				name: 'forum-file-by-id',
				params: { id: this.forum.thread_id },
			})
		},
		isCurrentUser(userId) {
			if (!this.profile) {
				return false
			}

			const { user_id: currentUserId } = this.profile
			return userId === currentUserId
		},
	},
	computed: {
		...mapGetters('auth', ['profile']),
		...mapGetters('app', ['hasOtherProfileAccess']),
		createdTime() {
			return calcDateAgo(this.forum.publish_timestamp)
		},
		currentUser() {
			return this.profile
		},
	},
	created() {
		if (this.forum) {
			this.isLiked = this.forum.upvote
			this.likes = this.forum.upvote_count
			this.bookmarked = this.forum.bookmark.is_bookmarked
		}
	},
}
</script>

<style lang="scss">
.forum-preview-card {
	background: #f1f1f1 !important;
	&__follow {
		display: flex;
		align-items: center;
		gap: 8px;
		&__status {
			font-family: 'Inter', sans-serif;
			font-weight: 700;
			font-size: 18px;
			line-height: 100%;
			letter-spacing: 0;
			text-align: center;
			color: var(--primary_color);
		}
	}
	&__container {
		width: 100%;
		height: 100%;
		border-radius: 8px !important;
		background: #ffffff !important;
		border: 1px solid #e2e2e2 !important;
	}

	&__header {
		display: flex;
		justify-content: space-between;
		align-items: center;

		@media (max-width: 480px) {
			flex-direction: column;
		}
	}

	&__meta {
		display: flex;
		justify-content: space-between;
		gap: 8px;
		flex-direction: column;
		word-break: break-word;

		&-bottom {
			color: #9b9b9b;
			font-family: Inter, sans-serif;
			font-weight: 400;
			font-size: 15px;
			line-height: 100%;
			letter-spacing: 0;
		}
		&-top {
			font-family: Inter, sans-serif;
			font-weight: 700;
			font-size: 18px;
			line-height: 100%;
			letter-spacing: 0;
			color: var(--primary_color);
		}
	}

	&__outer {
	}

	&__list {
		position: relative;
		flex-direction: column;
		&__info {
			display: flex;
			position: relative;
			flex-direction: column;
			gap: 8px;
			padding: 12px;
		}
		&__icons-container {
			display: flex;
			align-items: center;
			gap: 16px;
			&__icon-info {
				display: flex;
				align-items: center;
				gap: 4px;
				&__counter {
					font-family: Inter, sans-serif;
					font-weight: 700;
					font-size: 13px;
					line-height: 100%;
					letter-spacing: 0;
					color: #9b9b9b;
				}
			}
		}
	}
	&__title {
		color: var(--primary_color) !important;
		font-family: 'Inter', sans-serif;
		font-weight: 700;
		font-size: 20px;
		line-height: 22px;
		letter-spacing: 0;
		cursor: pointer;
	}
	&__content {
		font-family: 'Inter', sans-serif;
		font-style: normal;
		font-weight: 500;
		font-size: 16px;
		line-height: 21px;
	}

	&__thumbnail {
		height: auto;
		width: 100%;
	}

	&__menu-dots {
		margin-left: 10px !important;
	}

	@media screen and (max-width: 1904px) {
		&__block {
			flex: 0 0 100%;
		}
		&__list {
			flex: 0 0 75%;
		}
	}
	@media screen and (max-width: 1264px) {
		&__block {
			flex: 0 0 100%;
		}
		&__list {
			flex: 0 0 66%;
		}
	}

	@media screen and (min-width: 1264px) {
		&__container {
			max-width: 600px;
		}
	}
	@media screen and (max-width: 768px) {
		&__thumbnail {
			height: 100%;
			max-width: 100%;
		}
		&__default-thumbnail {
			max-height: 250px;
			height: 100%;
		}
		&__outer {
			flex-direction: column;
		}
	}
	&__image-container {
		display: flex;
		height: 56px;
		width: 100%;
		align-items: center;
		gap: 8px;
		&__item {
			position: relative;
			width: 99px;
			height: 56px;
			flex: 0 0 99px;
			cursor: pointer;
			border-radius: 5.39px;
			overflow: hidden;
		}
		&__img {
			height: 56px;
			width: 100%;
			border-radius: 5.39px;
			object-fit: inherit;
			border: 1px solid #e2e2e2;
		}
		&__overlay {
			position: absolute;
			inset: 0;
			display: flex;
			align-items: center;
			justify-content: center;
			background: rgba(0, 0, 0, 0.45);
			color: #ffffff;
			font-family: Inter, sans-serif;
			font-weight: 700;
			font-size: 16px;
			line-height: 1;
			border-radius: 5.39px;
		}
	}
}
.v-application p {
	margin-bottom: 0;
}
</style>
